// ─── New Version Banner ───────────────────────────────────────
// Non-blocking pill shown at the top of the editor when the
// deployed bundle hash differs from the one this tab is running.
// Honors unsaved changes: copy switches to "Save changes first"
// when `dirty=true` and the Refresh button is disabled until the
// designer saves (so a reload never blows away in-progress work).

import React, { useEffect, useState } from 'react';
import { RefreshCw, X } from 'lucide-react';
import { isClientOutOfDate } from '../../lib/utils/buildVersionCheck';

const POLL_INTERVAL_MS = 5 * 60 * 1000;       // every 5 min
const FIRST_CHECK_DELAY_MS = 4 * 1000;        // wait 4s post-mount so first paint isn't blocked
const DISMISS_KEY = 'sitoa.new_version.dismissed_for';

export default function NewVersionBanner({ dirty = false }) {
  const [outOfDate, setOutOfDate] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let stopped = false;
    let interval = null;

    const check = async () => {
      try {
        const stale = await isClientOutOfDate();
        if (stopped || !stale) return;
        // Respect a previous "Later" dismissal for THIS session only.
        // sessionStorage clears with the tab so reopening sitoa.studio
        // surfaces the banner again — which is what we want.
        const dismissedThisSession = (() => {
          try { return sessionStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
        })();
        if (!dismissedThisSession) setOutOfDate(true);
      } catch { /* ignore — never throw to the editor */ }
    };

    const t = setTimeout(() => {
      check();
      interval = setInterval(check, POLL_INTERVAL_MS);
    }, FIRST_CHECK_DELAY_MS);

    return () => {
      stopped = true;
      clearTimeout(t);
      if (interval) clearInterval(interval);
    };
  }, []);

  if (!outOfDate || dismissed) return null;

  const handleRefresh = () => {
    if (dirty) return; // safety — button is also disabled when dirty
    // Hard reload: bypass HTTP cache + force the SW to reactivate.
    try { window.location.reload(); } catch { /* swallow */ }
  };

  const handleLater = () => {
    setDismissed(true);
    try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch { /* private mode etc */ }
  };

  return (
    <div
      className="fixed top-3 left-1/2 -translate-x-1/2 z-[55] max-w-[440px] w-[min(440px,calc(100vw-24px))] rounded-xl border border-violet-200 dark:border-violet-700 bg-white dark:bg-gray-900 shadow-lg overflow-hidden"
      data-testid="new-version-banner"
      role="status"
    >
      <div className="flex items-center gap-3 px-3.5 py-2.5">
        <span className="flex-shrink-0 w-7 h-7 rounded-full bg-violet-100 dark:bg-violet-900/40 flex items-center justify-center">
          <RefreshCw className="w-3.5 h-3.5 text-violet-600 dark:text-violet-300" />
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-[12.5px] font-medium text-gray-900 dark:text-gray-100 leading-tight">
            A newer version of Studio is available
          </p>
          <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5 leading-snug">
            {dirty
              ? 'Save your changes, then refresh to load the latest fixes.'
              : 'Refresh to load the latest fixes.'}
          </p>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={handleRefresh}
            disabled={dirty}
            className="text-[11px] font-medium px-2.5 py-1.5 rounded-md bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            data-testid="new-version-refresh"
            title={dirty ? 'Save your unsaved changes first' : 'Reload Studio'}
          >
            Refresh
          </button>
          <button
            onClick={handleLater}
            className="text-[11px] text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-100 px-2 py-1.5 transition-colors"
            data-testid="new-version-later"
          >
            Later
          </button>
          <button
            onClick={handleLater}
            className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
            aria-label="Dismiss"
            data-testid="new-version-dismiss"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
